import { BackendHttpService } from "../../../../../services/backendHttpService";
import { TechnologyService } from "../../../../../services/technologyService";
import {
  NewProjectRequest,
  ProjectExperience,
  ProjectExperienceFormData,
  TechnologyWithFavorite,
  UpdateProjectRequest,
} from "../models/projectExperience.model";

export class ProjectExperienceService extends BackendHttpService {
  private technologyService = new TechnologyService();

  constructor() {
    super("project");
  }

  /**
   * Get all project experiences for a user
   * @param userId The user ID
   * @returns Promise with array of project experiences
   */
  async getProjectExperiences(userId: number): Promise<ProjectExperience[]> {
    try {
      return await this.get<ProjectExperience[]>(`/byUserId/${userId}`);
    } catch (error) {
      console.error(
        `Error getting project experiences for user ${userId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Add a new project experience
   * @param formData The form data to create project experience from
   * @param userId The user ID
   * @returns Promise with the created project experience
   */
  async addProjectExperience(
    formData: ProjectExperienceFormData,
    userId: number
  ): Promise<ProjectExperience> {
    try {
      // Convert technology IDs to PersonTechnology objects for backend
      const technologies = formData.technologies.map(
        (techWithFav: TechnologyWithFavorite) => {
          if (!techWithFav.id) {
            throw new Error(
              `Technology ID is required for: ${techWithFav.name}`
            );
          }

          return {
            id: 0, // Will be set by backend
            technology: {
              id: techWithFav.id,
              name: techWithFav.name,
              categories: [], // Categories will be populated by backend
            },
            personId: userId,
            favourite: techWithFav.isFavorite,
            skillLevel: 0,
            yearsOfExperience: 0,
            projects: [],
            workExperiences: [],
          };
        }
      );

      // Convert role names to NewRoleDto objects (POST uses NewRoleDto[])
      const roles = formData.roles.map((roleName) => ({
        name: roleName,
      }));

      // Convert responsibilities array to single string (backend expects string)
      const responsibilities = formData.responsibilities.join("\n");

      // Create NewProjectRequestDto payload
      const payload: NewProjectRequest = {
        customerName: formData.customerName,
        personId: userId,
        technologies,
        info: {
          description: formData.description,
          name: formData.projectName,
        },
        endDate:
          !formData.toMonth || !formData.toYear
            ? undefined
            : new Date(
              parseInt(formData.toYear),
              parseInt(formData.toMonth) - 1,
              1
            ).toISOString(),
        startDate: new Date(
          parseInt(formData.fromYear),
          parseInt(formData.fromMonth) - 1,
          1
        ).toISOString(),
        roles,
        responsibilities,
      };

      return await this.post<NewProjectRequest, ProjectExperience>(payload);
    } catch (error) {
      console.error("Error adding project experience:", error);
      throw error;
    }
  }

  /**
   * Update an existing project experience
   * @param project The project experience to update
   * @returns Promise with the updated project experience
   */
  async updateProjectExperience(
    project: ProjectExperience
  ): Promise<ProjectExperience> {
    try {
      // Create UpdateProjectRequest payload (ProjectDto)
      const payload: UpdateProjectRequest = {
        id: parseInt(project.id),
        customerId: project.customerId,
        customerName: project.customerName,
        internalProjectId: project.internalProjectId,
        personId: project.personId,
        technologies: project.technologies,
        info: project.info,
        endDate: project.endDate,
        startDate: project.startDate,
        roles: project.roles, // PUT uses RoleDto[] which includes id property
        responsibilities: project.responsibilities,
        internalProject: project.internalProject,
      };

      return await this.put<UpdateProjectRequest, ProjectExperience>(
        payload,
        `/${project.id}`
      );
    } catch (error) {
      console.error("Error updating project experience:", error);
      throw error;
    }
  }

  /**
   * Delete a project experience
   * @param projectId The ID of the project experience to delete
   * @returns Promise that resolves when deletion is complete
   */
  async deleteProjectExperience(projectId: string): Promise<void> {
    try {
      await this.delete(`/${projectId}`);
    } catch (error) {
      console.error("Error deleting project experience:", error);
      throw error;
    }
  }

  /**
   * Convert backend ProjectExperience to form data for editing
   * @param project The project experience from backend
   * @returns Form data for UI
   */
  projectToFormData(project: ProjectExperience): ProjectExperienceFormData {
    const startDate = new Date(project.startDate);
    const endDate = project.endDate ? new Date(project.endDate) : null;

    // Extract technology IDs, names, and favorite flags
    const technologies: TechnologyWithFavorite[] = (project.technologies || [])
      .map((pt) => ({
        id: pt.technology?.id || 0,
        name: pt.technology?.name || "",
        isFavorite: pt.favourite || false,
      }))
      .filter((tech) => tech.name !== "" && tech.id > 0);

    // Extract role names
    const roles = (project.roles || [])
      .map((role) => role.name)
      .filter(Boolean);

    // Convert responsibilities string to array (split by newlines and filter empty)
    const responsibilities = project.responsibilities
      ? project.responsibilities.split("\n").filter((r) => r.trim() !== "")
      : [];

    const formData: ProjectExperienceFormData = {
      id: project.id,
      customerName: project.customerName || "",
      projectName: project.info?.name || "",
      description: project.info?.description || "",
      responsibilities,
      roles,
      technologies,
      fromMonth: (startDate.getMonth() + 1).toString().padStart(2, "0"),
      fromYear: startDate.getFullYear().toString(),
      toMonth: endDate
        ? (endDate.getMonth() + 1).toString().padStart(2, "0")
        : "",
      toYear: endDate ? endDate.getFullYear().toString() : "",
    };

    return formData;
  }

  // CvDataService implementation
  async getDataByPersonID(personId: number): Promise<ProjectExperience[]> {
    return this.getProjectExperiences(personId);
  }

  async addData(
    data: ProjectExperienceFormData,
    personId: number
  ): Promise<ProjectExperience> {
    return this.addProjectExperience(data, personId);
  }

  async updateData(data: ProjectExperience): Promise<ProjectExperience> {
    return this.updateProjectExperience(data);
  }

  async deleteData(data: ProjectExperience): Promise<void> {
    return this.deleteProjectExperience(data.id);
  }
}
